import { BulkImportStatus, BulkImportType, GraphDataResponse, UploadStatusOptions } from '@dr-customer-offers-ui/lib-interfaces';
import { createReducer, on } from '@ngrx/store';
import { NgxDropdownOption } from 'ngx-dropdown';
import { OffersDataInterface, RegConfig } from '../../shared/models';
import { Role } from '../../shared/models/role';
import { User } from '../../shared/models/user.model';
import * as Actions from '../actions/com.actions';

export type ResourceKey =
  | 'user'
  | 'roles'
  | 'programs'
  | 'registrations'
  | 'regViewConfig'
  | 'graphOffers'
  | 'openOffers'
  | 'clearedOffers'
  | 'clearedGraphOffers'
  | 'scheduledOffers'
  | 'baselineOffers'
  | 'daOffers';

export interface RequestState {
  loading: boolean;
  loaded: boolean;
  error: string | null;
}

export interface BulkImportState {
  uploadStatus: UploadStatusOptions;
  uploadProgress: number;
  lastImportStatus: BulkImportStatus | null;
  percent: number;
  totalRows?: number;
  rowsProcessed: number;
  importType: BulkImportType | null;
  importId: string | null;
  registrationId: string | null;
}

export interface RegViewConfigCacheEntry {
  config: RegConfig;
  loadedAt: number;
}

export interface State {
  user: User | null;
  roles: Role[];
  programs: NgxDropdownOption[];
  registrations: NgxDropdownOption[];
  selectedProgram: NgxDropdownOption | null;
  selectedRegistration: NgxDropdownOption | null;
  graphOffers: GraphDataResponse | null;
  clearedGraphOffers: GraphDataResponse | null;
  currentGraphOffers: GraphDataResponse | null;
  currentClearedGraphOffers: GraphDataResponse | null;
  currentOpenOffers: OffersDataInterface | null;
  currentClearedOffers: OffersDataInterface | null;
  currentScheduledOffers: OffersDataInterface | null;
  currentBaselineOffers: OffersDataInterface | null;
  currentDAOffers: OffersDataInterface | null;

  // Cache of RegViewConfigs keyed by registrationId
  regViewConfigCache: Record<string, RegViewConfigCacheEntry>;

  // Current active regViewConfig (for the selected registration)
  currentRegViewConfig: RegConfig | null;
  currentRegViewConfigId: string | null;

  // Bulk import
  bulkImport: BulkImportState;

  // one common place for all loading/error/loaded flags
  requests: Record<ResourceKey, RequestState>;
}

const blankReq: RequestState = { loading: false, loaded: false, error: null };

export const initialState: State = {
  user: null,
  roles: [],
  programs: [],
  registrations: [],
  selectedProgram: null,
  selectedRegistration: null,
  graphOffers: null,
  regViewConfigCache: {},
  currentRegViewConfig: null,
  currentRegViewConfigId: null,
  currentGraphOffers: null,
  currentOpenOffers: null,
  currentClearedOffers: null,
  clearedGraphOffers: null,
  currentClearedGraphOffers: null,
  currentScheduledOffers: null,
  currentBaselineOffers: null,
  currentDAOffers: null,
  bulkImport: {
    uploadStatus: UploadStatusOptions.None,
    uploadProgress: 0,
    lastImportStatus: null,
    importType: null,
    importId: null,
    registrationId: null,
    percent: 0,
    totalRows: undefined,
    rowsProcessed: 0
  },
  requests: {
    user: { ...blankReq },
    roles: { ...blankReq },
    programs: { ...blankReq },
    registrations: { ...blankReq },
    regViewConfig: { ...blankReq },
    graphOffers: { ...blankReq },
    openOffers: { ...blankReq },
    clearedOffers: { ...blankReq },
    clearedGraphOffers: { ...blankReq },
    scheduledOffers: { ...blankReq },
    baselineOffers: { ...blankReq },
    daOffers: { ...blankReq }
  }
};

export const Reducer = createReducer(
  initialState,

  // USER
  on(Actions.userReceived, (state: State, { user }) => ({
    ...setSuccess(state, 'user'),
    user: user
  })),

  // USER ROLES
  on(Actions.rolesReceived, (state: State, { roles }) => ({
    ...setSuccess(state, 'roles'),
    roles
  })),

  // Programs
  on(Actions.loadPrograms, (state: State) => setLoading(state, 'programs')),
  on(Actions.loadProgramsSuccess, (state: State, { programs, selectedProgram }) => ({
    ...setSuccess(state, 'programs'),
    programs,
    selectedProgram
  })),
  on(Actions.loadProgramsFailure, (state: State, { error }) => setFailure(state, 'programs', error)),

  // Registrations
  on(Actions.loadRegistrations, (state: State) => setLoading(state, 'registrations')),
  on(Actions.loadRegistrationsSuccess, (state: State, { registrations, selectedRegistration }) => ({
    ...setSuccess(state, 'registrations'),
    registrations,
    selectedRegistration
  })),
  on(Actions.loadRegistrationsFailure, (state: State, { error }) => setFailure(state, 'registrations', error)),

  // RegView Config
  on(Actions.loadRegViewConfig, (state: State, { registrationId }) => ({
    ...setLoading(state, 'regViewConfig'),
    currentRegViewConfigId: registrationId
  })),

  on(Actions.loadRegViewConfigSuccess, (state: State, { regViewConfig, registrationId }) => ({
    ...setSuccess(state, 'regViewConfig'),
    currentRegViewConfig: { ...regViewConfig },
    currentRegViewConfigId: registrationId,
    regViewConfigCache: {
      ...state.regViewConfigCache,
      [registrationId]: {
        config: regViewConfig,
        loadedAt: Date.now()
      }
    }
  })),

  on(Actions.loadRegViewConfigFailure, (state: State, { error }) => setFailure(state, 'regViewConfig', error)),

  // Graph Offers
  on(Actions.loadGraphOffers, (state: State) => setLoading(state, 'graphOffers')),

  on(Actions.loadGraphOffersSuccess, (state: State, { graphOffers }) => ({
    ...setSuccess(state, 'graphOffers'),
    graphOffers,
    currentGraphOffers: graphOffers
  })),

  on(Actions.loadGraphOffersFailure, (state: State, { error }) => setFailure(state, 'graphOffers', error)),

  // Open Offers
  on(Actions.loadOpenOffers, (state: State) => setLoading(state, 'openOffers')),

  on(Actions.loadOpenOffersSuccess, (state: State, { offers }) => ({
    ...setSuccess(state, 'openOffers'),
    currentOpenOffers: offers
  })),

  on(Actions.loadOpenOffersFailure, (state: State, { error }) => setFailure(state, 'openOffers', error)),

  // Cleared Offers
  on(Actions.loadClearedOffers, (state: State) => setLoading(state, 'clearedOffers')),

  on(Actions.loadClearedOffersSuccess, (state: State, { offers }) => ({
    ...setSuccess(state, 'clearedOffers'),
    currentClearedOffers: offers
  })),

  on(Actions.loadClearedOffersFailure, (state: State, { error }) => setFailure(state, 'clearedOffers', error)),

  // Cleared Graph Offers
  on(Actions.loadClearedGraphOffers, (state: State) => setLoading(state, 'clearedGraphOffers')),

  on(Actions.loadClearedGraphOffersSuccess, (state: State, { graphOffers }) => ({
    ...setSuccess(state, 'clearedGraphOffers'),
    clearedGraphOffers: graphOffers,
    currentClearedGraphOffers: graphOffers
  })),

  on(Actions.loadClearedGraphOffersFailure, (state: State, { error }) => setFailure(state, 'clearedGraphOffers', error)),

  // Schedule Offers
  on(Actions.loadScheduledOffers, (state: State) => setLoading(state, 'scheduledOffers')),

  on(Actions.loadScheduledOffersSuccess, (state: State, { offers }) => ({
    ...setSuccess(state, 'scheduledOffers'),
    currentScheduledOffers: offers
  })),

  on(Actions.loadScheduledOffersFailure, (state: State, { error }) => setFailure(state, 'scheduledOffers', error)),

  // Baseline Offers
  on(Actions.loadBaselineOffers, (state: State) => setLoading(state, 'baselineOffers')),

  on(Actions.loadBaselineOffersSuccess, (state: State, { offers }) => ({
    ...setSuccess(state, 'baselineOffers'),
    currentBaselineOffers: offers
  })),

  on(Actions.loadBaselineOffersFailure, (state: State, { error }) => setFailure(state, 'baselineOffers', error)),

  // DA Offers
  on(Actions.loadDAOffers, (state: State) => setLoading(state, 'daOffers')),

  on(Actions.loadDAOffersSuccess, (state: State, { offers }) => ({
    ...setSuccess(state, 'daOffers'),
    currentDAOffers: offers
  })),

  on(Actions.loadDAOffersFailure, (state: State, { error }) => setFailure(state, 'daOffers', error)),

  // Bulk Import
  on(Actions.initiateBulkImport, (state: State, { importType }) => ({
    ...state,
    bulkImport: { ...state.bulkImport, uploadStatus: UploadStatusOptions.Uploading, uploadProgress: 0, importType }
  })),

  on(Actions.bulkImportInitSuccess, (state: State, { initResponse }) => ({
    ...state,
    bulkImport: { ...state.bulkImport, importId: initResponse.import_id, registrationId: initResponse.registration_id }
  })),

  on(Actions.bulkImportUploadProgress, (state: State, { progress }) => ({
    ...state,
    bulkImport: { ...state.bulkImport, uploadProgress: progress }
  })),

  on(Actions.bulkImportUploadComplete, (state: State) => ({
    ...state,
    bulkImport: { ...state.bulkImport, uploadStatus: UploadStatusOptions.Processing, percent: 0 }
  })),

  on(Actions.bulkImportStatusReceived, (state: State, { status, percent, totalRows, rowsProcessed }) => ({
    ...state,
    bulkImport: { ...state.bulkImport, lastImportStatus: status, percent, totalRows, rowsProcessed }
  })),

  on(Actions.bulkImportCompleted, (state: State) => ({
    ...state,
    bulkImport: { ...state.bulkImport, uploadStatus: UploadStatusOptions.None }
  })),

  on(Actions.bulkImportFailed, (state: State) => ({
    ...state,
    bulkImport: { ...state.bulkImport, uploadStatus: UploadStatusOptions.None }
  })),

  on(Actions.bulkImportReset, (state: State) => ({
    ...state,
    bulkImport: {
      uploadStatus: UploadStatusOptions.None,
      uploadProgress: 0,
      lastImportStatus: null,
      importType: null,
      importId: null,
      registrationId: null,
      percent: 0,
      totalRows: undefined,
      rowsProcessed: 0
    }
  }))
);
function setLoading(state: State, key: ResourceKey): State {
  return {
    ...state,
    requests: {
      ...state.requests,
      [key]: { loading: true, loaded: false, error: null }
    }
  };
}

function setSuccess(state: State, key: ResourceKey): State {
  return {
    ...state,
    requests: {
      ...state.requests,
      [key]: { loading: false, loaded: true, error: null }
    }
  };
}

function setFailure(state: State, key: ResourceKey, error: string): State {
  return {
    ...state,
    requests: {
      ...state.requests,
      [key]: { loading: false, loaded: false, error }
    }
  };
}
